import type { ChekiType } from '@tsuyosa/types';

interface ScheduleSummary { title: string; date: string; venue?: string | null; }
interface MemberItem      { name: string; quantity: number; }

interface Props {
  schedule:    ScheduleSummary;
  type:        ChekiType;
  memberItems: MemberItem[] | undefined;
  quantity:    number;
  totalPrice:  number;
  buyerName:   string;
  email:       string;
  contact:     string;
  isLoading:   boolean;
  error:       string;
  onBack:      () => void;
  onConfirm:   () => void;
}

function formatDate(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('id-ID', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  });
}
function formatPrice(n: number) {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);
}

export function ReviewStep({
  schedule, type, memberItems, quantity, totalPrice,
  buyerName, email, contact, isLoading, error, onBack, onConfirm,
}: Props) {
  return (
    <div className="min-h-screen px-4 py-8">
      <div className="mx-auto max-w-md">
        <p className="mb-6 text-[10px] uppercase tracking-[0.3em] text-white/40">Review Pesanan</p>

        <div
          className="flex flex-col gap-4 rounded-xl border p-5"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <div>
            <p className="text-xs text-white/40">Jadwal</p>
            <p className="font-semibold text-white">{schedule.title}</p>
            <p className="text-sm text-white/50">
              {formatDate(schedule.date)}{schedule.venue ? ` · ${schedule.venue}` : ''}
            </p>
          </div>

          <div className="border-t" style={{ borderColor: 'var(--border)' }} />

          <div className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-white/40">Tipe</span>
              <span className="text-white">{type === '2shot_solo' ? '2-Shot Solo' : 'All Member'}</span>
            </div>
            {memberItems ? memberItems.map((m, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-white/40">{m.name}</span>
                <span className="text-white">×{m.quantity}</span>
              </div>
            )) : (
              <div className="flex justify-between">
                <span className="text-white/40">Jumlah</span>
                <span className="text-white">{quantity} lembar</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span className="text-white/40">Total</span>
              <span className="text-white">{formatPrice(totalPrice)}</span>
            </div>
          </div>

          <div className="border-t" style={{ borderColor: 'var(--border)' }} />

          <div className="flex flex-col gap-1 text-sm">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Data Pemesan</p>
            <p className="text-white">{buyerName}</p>
            <p className="text-white/60">{email}</p>
            {contact && <p className="text-white/60">{contact}</p>}
          </div>
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-red-500/10 px-3 py-2.5 text-sm text-red-400">{error}</p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 rounded-xl border py-3 text-sm text-white/60 transition hover:text-white"
            style={{ borderColor: 'var(--border)' }}
          >
            ← Edit
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-[2] flex items-center justify-center rounded-xl py-3 font-semibold text-white transition disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, var(--pink), var(--pink-dark, #be185d))' }}
          >
            {isLoading
              ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              : 'Lanjut ke Pembayaran →'}
          </button>
        </div>
      </div>
    </div>
  );
}
